import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, SpinLoading } from 'antd-mobile';
import 'antd-mobile/es/components/button/button.css';
import 'antd-mobile/es/components/spin-loading/spin-loading.css';
import { authApi, wxApi } from '../../api/client';
import { Icon } from '../../components/Icon';

const TENANT_ID = 'TENANT-A';
const MP_NAME = '盒智家玺越创智';
const ALLOW_CODE_LOGIN = import.meta.env.VITE_ALLOW_CODE_LOGIN === 'true';

type Stage = 'idle' | 'loading' | 'follow' | 'bind' | 'done' | 'error';

function isWeChat(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /micromessenger/i.test(navigator.userAgent.toLowerCase());
}

function getRedirect(): string {
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const redirect = params.get('redirect');
  if (redirect && redirect.startsWith('/')) return redirect;
  return '/';
}

// 微信未关注时，将当前页 redirect 传递给 OAuth 回调，登录成功后跳回原页
function getWxRedirectParam(): string {
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const redirect = params.get('redirect');
  // 微信回调需要回到本页，本页已在 URL 中携带 redirect 参数
  const authPath = redirect
    ? `/auth?redirect=${encodeURIComponent(redirect)}`
    : '/auth';
  return authPath;
}

// 关注/绑定引导卡片（服务号二维码 + 长按提示）
function FollowGuide({ mpName, qrUrl, bindOnly, polling }: {
  mpName: string; qrUrl: string; bindOnly?: boolean; polling?: boolean;
}) {
  return (
    <div style={{ textAlign: 'center', padding: '8px 4px' }}>
      <div style={{ width: 64, height: 64, borderRadius: 16,
        background: bindOnly ? 'var(--color-primary)' : '#07c160',
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '4px auto 14px' }}>
        <Icon name="book-open" size={30} color="#fff" />
      </div>
      <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
        {bindOnly ? '请完成学员身份注册' : '请先关注服务号'}
      </p>
      {qrUrl && (
        <img
          src={qrUrl}
          alt="服务号二维码"
          style={{ width: 168, height: 168, margin: '8px auto 12px', display: 'block',
            border: '1px solid var(--gray-200)', borderRadius: 8 }}
        />
      )}
      <p style={{ fontSize: 13, color: 'var(--gray-600)', lineHeight: 1.8, marginBottom: 6 }}>
        {bindOnly ? (
          <>关注后，在「<b style={{ color: 'var(--color-primary)' }}>{mpName}</b>」服务号对话框中<br />私信发送您的<b>企业学员号</b>（如 <b>ABC001</b>），<br />核验通过后自动完成注册登录。</>
        ) : qrUrl ? (
          <>长按上方二维码，识别并关注「<b style={{ color: 'var(--color-primary)' }}>{mpName}</b>」服务号，<br />关注后在服务号内私信发送您的<b>企业学员号</b><br />（如 <b>ABC001</b>）完成注册登录。</>
        ) : (
          <>请在微信中搜索并关注「<b style={{ color: 'var(--color-primary)' }}>{mpName}</b>」服务号，<br />关注后在服务号内私信发送您的<b>企业学员号</b><br />（如 <b>ABC001</b>）完成注册登录。</>
        )}
      </p>
      {qrUrl && (
        <p style={{ fontSize: 12, color: '#07c160', marginTop: 8 }}>
          {bindOnly ? '↑ 长按二维码进入服务号发送学员号' : '↑ 长按二维码识别关注'}
        </p>
      )}
      <p style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 14, lineHeight: 1.6 }}>
        {polling ? '操作完成后将自动检测并登录，请稍候...' : '完成操作后请回到此页面，系统会自动检测登录状态'}
      </p>
    </div>
  );
}

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState('');
  const [tenantId] = useState(TENANT_ID);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // 从 URL 读取提示（如账号停用、未登录跳转）
  const tip = searchParams.get('tip');
  useEffect(() => {
    if (tip) setError(tip);
  }, [tip]);
  const [stage, setStage] = useState<Stage>('idle');
  const [wxLoading, setWxLoading] = useState(false);
  const [mpName, setMpName] = useState(MP_NAME);
  const [polling, setPolling] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const openidRef = useRef<string | null>(null);
  const stageRef = useRef<Stage>('idle');
  const pollTimer = useRef<number | null>(null);
  const inWeChat = isWeChat();

  useEffect(() => { stageRef.current = stage; }, [stage]);

  // 加载服务号关注二维码（微信内长按识别关注）
  useEffect(() => {
    wxApi.followQrcode()
      .then((d) => { if (d.url) setQrUrl(d.url); if (d.mp_name) setMpName(d.mp_name); })
      .catch(() => { /* 二维码加载失败时保留文字引导 */ });
  }, []);

  // 解析 OAuth 回调参数（微信内一键登录返回）
  useEffect(() => {
    const resultParam = searchParams.get('wx_result');
    if (resultParam) {
      try {
        const result = JSON.parse(decodeURIComponent(resultParam));
        const newUrl = window.location.pathname + (getRedirect() !== '/' ? `?redirect=${encodeURIComponent(getRedirect())}` : '');
        window.history.replaceState({}, '', newUrl);
        handleWxResult(result);
        return;
      } catch { /* ignore */ }
    }
    // 也检查 ?code=xxx&state=xxx（直接从微信 OAuth 跳回，未经后端 302）
    const wxCode = searchParams.get('code');
    if (wxCode && inWeChat && !localStorage.getItem('emp_token')) {
      completeWxLogin(wxCode);
      return;
    }
    // 换绑自愈：40303 清旧 token 后带 wx=1 回到本页，自动发起一次网页授权换新 token
    const forceWx = searchParams.get('wx') === '1';
    if (forceWx && inWeChat && !wxCode && !localStorage.getItem('emp_token')) {
      handleWxLogin();
      return;
    }
    // 已登录直接跳走
    if (localStorage.getItem('emp_token')) {
      navigate(getRedirect(), { replace: true });
    }
  }, [searchParams, navigate, inWeChat]);

  // 轮询注册状态（已关注 + 已绑定）
  useEffect(() => {
    if (!polling || !openidRef.current) return;
    const openid = openidRef.current;
    if (pollTimer.current) window.clearInterval(pollTimer.current);
    pollTimer.current = window.setInterval(async () => {
      if (stageRef.current !== 'follow' && stageRef.current !== 'bind') return;
      try {
        const status = await wxApi.registerStatus(openid);
        if (status.ready && status.access_token) {
          setPolling(false);
          if (pollTimer.current) window.clearInterval(pollTimer.current);
          localStorage.removeItem('emp_tenant_id');
          localStorage.setItem('emp_token', status.access_token);
          if (status.expires_at) {
            localStorage.setItem('emp_token_exp', String(status.expires_at));
          }
          setStage('done');
          setTimeout(() => navigate(getRedirect(), { replace: true }), 500);
        } else if (status.bound && status.subscribed && status.access_token) {
          setPolling(false);
          if (pollTimer.current) window.clearInterval(pollTimer.current);
          localStorage.removeItem('emp_tenant_id');
          localStorage.setItem('emp_token', status.access_token);
          setStage('done');
          setTimeout(() => navigate(getRedirect(), { replace: true }), 500);
        } else if (status.subscribed && !status.bound && stageRef.current === 'follow') {
          // 已关注但未绑定 → 切换到"私信学员号"引导
          setStage('bind');
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => {
      if (pollTimer.current) window.clearInterval(pollTimer.current);
    };
  }, [polling, navigate]);

  function handleWxResult(result: {
    bound?: boolean; needs_bind?: boolean; subscribed?: number;
    access_token?: string; expires_at?: number; openid?: string;
    employee?: { code: string; public_id: string; tenant_id?: string };
    message?: string; mp_name?: string;
  }) {
    if (result.mp_name) setMpName(result.mp_name);
    // 已关注且已绑定 → 登录成功
    if (result.bound && result.subscribed === 1 && result.access_token) {
      localStorage.removeItem('emp_tenant_id'); // 清掉历史残留的旧租户头
      localStorage.setItem('emp_token', result.access_token);
      if (result.expires_at) localStorage.setItem('emp_token_exp', String(result.expires_at));
      // 注意：kc_wx_reauth 标记不在此处清除——由 client.ts 在后续请求成功时清除，
      // 避免真·他企账号换新 token 后仍 403 时被反复重授权（死循环）。
      setStage('done');
      setWxLoading(false);
      setTimeout(() => navigate(getRedirect(), { replace: true }), 500);
      return;
    }
    // 已关注但未绑定学员号
    if (result.subscribed === 1 && result.openid) {
      openidRef.current = result.openid;
      setStage('bind');
      setWxLoading(false);
      setPolling(true);
      return;
    }
    // 未关注
    if (result.subscribed === 0 && result.openid) {
      openidRef.current = result.openid;
      setStage('follow');
      setWxLoading(false);
      setPolling(true);
      return;
    }
    setWxLoading(false);
    setError(result.message || '微信登录失败，请重试');
    setStage('error');
  }

  async function completeWxLogin(wxCode: string) {
    setWxLoading(true);
    setStage('loading');
    try {
      const result = await wxApi.completeOauth(wxCode);
      handleWxResult(result);
    } catch (e) {
      setWxLoading(false);
      setError(e instanceof Error ? e.message : '微信登录失败');
      setStage('error');
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim()) { setError('请输入学员代码'); return; }
    setLoading(true);
    setError('');
    try {
      const result = await authApi.verify(code.trim().toUpperCase(), tenantId);
      localStorage.setItem('emp_token', result.access_token);
      localStorage.setItem('emp_token_exp', String(result.expires_at));
      localStorage.setItem('emp_code', result.employee?.code || code.trim().toUpperCase());
      setStage('done');
      setTimeout(() => navigate(getRedirect(), { replace: true }), 400);
    } catch (e) {
      setError(e instanceof Error ? e.message : '核验失败，请检查学员代码');
    } finally {
      setLoading(false);
    }
  }

  async function handleWxLogin() {
    setWxLoading(true);
    setError('');
    try {
      const result = await wxApi.oauthUrl(getWxRedirectParam());
      window.location.href = result.url;
    } catch (e) {
      setError(e instanceof Error ? e.message : '微信登录不可用');
      setWxLoading(false);
    }
  }

  // ---------- 渲染 ----------
  if (wxLoading || stage === 'loading') {
    return (
      <div className="auth-page">
        <div className="brand-logo"><Icon name="book-open" size={36} color="#fff" /></div>
        <h1>知识卡答题平台</h1>
        <SpinLoading style={{ '--size': '32px', margin: '16px auto' }} color="primary" />
        <p className="auth-subtitle">微信授权中，请稍候...</p>
      </div>
    );
  }

  if (stage === 'done') {
    return (
      <div className="auth-page">
        <div className="success-icon">
          <Icon name="check-circle" size={64} color="var(--success)" />
        </div>
        <h2 className="auth-title">登录成功</h2>
        <p>即将跳转到学习页面...</p>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="brand-logo"><Icon name="book-open" size={36} color="#fff" /></div>
      <h1>知识卡答题平台</h1>

      {error && <div className="auth-error" style={{ marginBottom: 12 }}>{error}</div>}

      {/* 微信内：未关注 → 引导关注服务号 */}
      {inWeChat && stage === 'follow' && (
        <FollowGuide mpName={mpName} qrUrl={qrUrl} polling={polling} />
      )}

      {/* 微信内：已关注未绑定 → 引导私信学员号 */}
      {inWeChat && stage === 'bind' && (
        <FollowGuide mpName={mpName} qrUrl={qrUrl} bindOnly polling={polling} />
      )}

      {/* 初始态 / 账号登录 */}
      {(stage === 'idle' || stage === 'error') && (
        <>
          {ALLOW_CODE_LOGIN && (
            <form onSubmit={handleSubmit} className="auth-form">
              <p className="auth-subtitle">本地联调：输入企业学员号登录</p>
              <div className="form-group">
                <label>学员代码</label>
                <input
                  className="auth-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="例如 ABC001"
                  autoComplete="username"
                  inputMode="text"
                  autoCapitalize="characters"
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>企业 ID</label>
                <input
                  className="auth-input"
                  value={tenantId}
                  readOnly
                  disabled
                  style={{ background: 'var(--gray-50)', color: 'var(--gray-500)' }}
                />
              </div>
              <Button type="submit" color="primary" block size="large" loading={loading} disabled={loading}>
                {loading ? '登录中...' : '登 录'}
              </Button>
            </form>
          )}

          {inWeChat ? (
            <>
              {ALLOW_CODE_LOGIN && <div className="auth-divider"><span>或</span></div>}
              <Button
                type="button"
                block
                size="large"
                loading={wxLoading}
                disabled={wxLoading}
                onClick={handleWxLogin}
                style={{ background: '#07c160', borderColor: '#07c160', color: '#fff' }}
              >
                {wxLoading ? '微信授权中...' : '微信一键登录'}
              </Button>
              <p style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 16, lineHeight: 1.7, textAlign: 'center' }}>
                首次使用请先关注「{mpName}」服务号并绑定学员号；<br />绑定后微信扫码可免登录答题
              </p>
            </>
          ) : (
            <FollowGuide mpName={mpName} qrUrl={qrUrl} polling={false} />
          )}
        </>
      )}
    </div>
  );
}
